import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { api } from '../utils/api';

// ========== SINGLETON SOCKET ==========
let socket = null;
let socketListeners = new Set();
let newsListeners = new Set();
let presenceListeners = new Set();
let presenceSnapshot = [];

export function connectSocket() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  if (socket) return socket;
  socket = io(window.location.origin, {
    auth: { token }, transports: ['websocket', 'polling'],
    reconnection: true, reconnectionDelay: 1000, reconnectionAttempts: 100,
  });
  socket.on('connect', () => console.log('🔌 Socket connected:', socket.id));
  socket.on('disconnect', (reason) => console.log('🔌 Socket disconnected:', reason));
  socket.on('new_message', (msg) => socketListeners.forEach(cb => { try { cb(msg); } catch(e) {} }));
  socket.on('news_published', (news) => newsListeners.forEach(cb => { try { cb(news); } catch(e) {} }));
  socket.on('presence_update', (list) => {
    presenceSnapshot = Array.isArray(list) ? list : [];
    presenceListeners.forEach(cb => { try { cb(presenceSnapshot); } catch(e) {} });
  });
  return socket;
}
export function disconnectSocket() {
  if (socket) { socket.removeAllListeners(); socket.disconnect(); socket = null; }
  presenceSnapshot = [];
  presenceListeners.forEach(cb => { try { cb([]); } catch(e) {} });
}
export function getSocket() { return socket; }
export function onNewMessage(cb) { socketListeners.add(cb); return () => socketListeners.delete(cb); }
export function onNewsPublished(cb) { newsListeners.add(cb); return () => newsListeners.delete(cb); }
export function onPresenceUpdate(cb) {
  presenceListeners.add(cb);
  cb(presenceSnapshot);
  return () => presenceListeners.delete(cb);
}

function useProtectedBlob(endpoint) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!endpoint) { setBlobUrl(null); return undefined; }
    setBlobUrl(null);
    let active = true;
    let objectUrl = null;
    const token = localStorage.getItem('token');
    fetch(endpoint, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(response => {
        if (!response.ok) throw new Error('Fichier inaccessible');
        return response.blob();
      })
      .then(blob => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => { if (active) setBlobUrl(null); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint]);

  return blobUrl;
}

function ProtectedRoomIcon({ room, className }) {
  const endpoint = room?.icon_path ? `/api/chat/rooms/${room.id}/icon?v=${encodeURIComponent(room.icon_path)}` : null;
  const blobUrl = useProtectedBlob(endpoint);
  if (!room?.icon_path) return room?.type === 'store' ? '🏪' : '💬';
  return blobUrl ? <img src={blobUrl} alt="" className={className} /> : '…';
}

function ProtectedAttachment({ message }) {
  const blobUrl = useProtectedBlob(message?.file_path ? `/api/chat/files/${message.id}` : null);
  if (!message?.file_path) return null;
  if (!blobUrl) return <span className="chat-bubble-file">📎 Chargement…</span>;
  if (message.file_type?.startsWith('image/')) {
    return <a href={blobUrl} target="_blank" rel="noreferrer"><img src={blobUrl} alt="" className="chat-bubble-img" /></a>;
  }
  return <a href={blobUrl} download={message.file_name || 'fichier'} className="chat-bubble-file">📎 {message.file_name || 'Fichier'}</a>;
}

// ========== EMOJI DATA ==========
const EMOJI_CATEGORIES = {
  '😀': ['😀','😂','🤣','😊','😍','🥰','😘','😜','🤔','😱','😢','😤','🥳','😎','🙄','😴','🤗','🤮','👍','👎','❤️','🔥','👏','🎉','💪','🙏','✨','💯','⭐','🌈'],
  '👋': ['👋','✌️','🤞','🤙','👌','🖐️','👊','✊','🤝','🫡','💅','🫶','🧠','👀','👁️','🦾'],
  '🎮': ['⚽','🏀','🎮','🎬','🎵','🎸','🎲','🏆','🥇','🎯','🎁','🎈','🎊'],
  '🍕': ['🍕','🍔','🍟','🍺','☕','🍰','🍩','🍿','🥐','🧀','🍷','🥂'],
  '💼': ['💼','📱','💻','📧','📞','✅','❌','⚠️','📌','🔔','💡','🔑','🛠️','📊','📈'],
};
const QUICK_REACTIONS = ['👍','❤️','😂','😮','😢','🔥'];

// ========== CHAT PAGE ==========
export default function ChatPage() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [roomIcon, setRoomIcon] = useState(null);
  const [roomIconPreview, setRoomIconPreview] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [members, setMembers] = useState([]);
  const [showRoomMenu, setShowRoomMenu] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showRemoveMember, setShowRemoveMember] = useState(false);
  const [showChangeIcon, setShowChangeIcon] = useState(false);
  const [showAdminVisibility, setShowAdminVisibility] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiCat, setEmojiCat] = useState('😀');
  const [replyTo, setReplyTo] = useState(null);
  const [editMsg, setEditMsg] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showReaders, setShowReaders] = useState(null);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeout = useRef(null);
  const currentRoomRef = useRef(null);
  const roomMenuRef = useRef(null);

  useEffect(() => { currentRoomRef.current = currentRoom; }, [currentRoom]);

  const loadRooms = useCallback(async () => {
    try { setRooms(await api.chatRooms()); } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  // Socket events
  useEffect(() => {
    const s = connectSocket();
    if (!s) return;

    const unsub = onNewMessage((m) => {
      if (currentRoomRef.current && m.room_id === currentRoomRef.current.id) {
        setMessages(prev => prev.some(p => p.id === m.id) ? prev : [...prev, m]);
        s.emit('mark_read', m.room_id);
      }
      loadRooms();
    });

    const onEdited = (m) => setMessages(prev => prev.map(p => p.id === m.id ? { ...p, content: m.content, edited_at: m.edited_at } : p));
    const onDeleted = (d) => setMessages(prev => prev.map(p => p.id === d.id ? { ...p, deleted_at: 'yes', content: '' } : p));
    const onReaction = (r) => {
      setMessages(prev => prev.map(p => {
        if (p.id !== r.message_id) return p;
        const reactions = { ...(p.reactions || {}) };
        const key = r.emoji;
        if (!reactions[key]) reactions[key] = [];
        if (!reactions[key].find(x => x.user_id === r.user_id)) {
          reactions[key] = [...reactions[key], { user_id: r.user_id, username: r.username }];
        }
        return { ...p, reactions };
      }));
    };
    const onPresence = (list) => setOnlineUserIds(list.map(u => u.id));
    const onTyping = (d) => {
      if (!currentRoomRef.current || d.room_id !== currentRoomRef.current.id) return;
      setTypingUsers(prev => {
        if (prev.find(t => t.user_id === d.user_id)) return prev;
        return [...prev, d];
      });
      setTimeout(() => setTypingUsers(prev => prev.filter(t => t.user_id !== d.user_id)), 2500);
    };
    const onReadUpdate = (d) => {
      if (!currentRoomRef.current || d.room_id !== currentRoomRef.current.id) return;
      setMessages(prev => prev.map(p => {
        if (p.read_by?.find(r => r.user_id === d.user_id)) return p;
        return { ...p, read_by: [...(p.read_by || []), { user_id: d.user_id, username: d.username }] };
      }));
    };

    s.on('message_edited', onEdited);
    s.on('message_deleted', onDeleted);
    s.on('message_reaction', onReaction);
    const unsubPresence = onPresenceUpdate(onPresence);
    s.on('user_typing', onTyping);
    s.on('read_update', onReadUpdate);

    return () => {
      unsub();
      s.off('message_edited', onEdited);
      s.off('message_deleted', onDeleted);
      s.off('message_reaction', onReaction);
      unsubPresence();
      s.off('user_typing', onTyping);
      s.off('read_update', onReadUpdate);
    };
  }, [loadRooms]);

  useEffect(() => {
    if (!currentRoom) { setMessages([]); setMembers([]); return; }
    api.chatMessages(currentRoom.id).then(setMessages).catch(() => {});
    api.chatMembers(currentRoom.id).then(setMembers).catch(() => {});
    const s = getSocket();
    if (s) s.emit('mark_read', currentRoom.id);
    loadRooms();
  }, [currentRoom, loadRooms]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Close context menu on click outside
  useEffect(() => {
    const handler = () => { setContextMenu(null); setShowRoomMenu(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleSend = () => {
    const s = getSocket();
    if (!s || !currentRoom) return;
    if (editMsg) {
      if (msg.trim()) s.emit('edit_message', { message_id: editMsg.id, content: msg.trim() });
      setEditMsg(null); setMsg('');
    } else {
      if (!msg.trim()) return;
      s.emit('send_message', { room_id: currentRoom.id, content: msg.trim(), reply_to_id: replyTo?.id });
      setReplyTo(null); setMsg('');
    }
    setShowEmoji(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    if (e.key === 'Escape') { setReplyTo(null); setEditMsg(null); setMsg(''); }
    const s = getSocket();
    if (s && currentRoom) { clearTimeout(typingTimeout.current); s.emit('typing', currentRoom.id); typingTimeout.current = setTimeout(() => {}, 2000); }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentRoom) return;
    setUploading(true);
    try {
      const result = await api.chatUpload(file, currentRoom.id);
      const s = getSocket();
      if (s) s.emit('send_message', { room_id: currentRoom.id, content: '', upload_token: result.upload_token });
    } catch (err) { toast('Erreur upload', 'error'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;
    try {
      await api.chatCreateRoom({ name: newRoomName, member_ids: selectedMembers, icon: roomIcon });
      toast('Salon créé', 'success');
      setShowCreateRoom(false); setNewRoomName(''); setSelectedMembers([]);
      setRoomIcon(null); setRoomIconPreview(null);
      loadRooms();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Supprimer ce salon et tous ses messages ?')) return;
    try { await api.chatDeleteRoom(roomId); if (currentRoom?.id === roomId) setCurrentRoom(null); loadRooms(); toast('Salon supprimé', 'success'); } catch (e) { toast(e.message, 'error'); }
    setShowRoomMenu(false);
  };

  const handleAddMembers = async (userIds) => {
    if (!currentRoom || !userIds.length) return;
    try {
      const updated = await api.chatAddMembers(currentRoom.id, userIds);
      setMembers(updated);
      toast(`${userIds.length} membre(s) ajouté(s)`, 'success');
      setShowAddMember(false);
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!currentRoom) return;
    const m = members.find(x => x.id === userId);
    if (!window.confirm(`Retirer ${m?.username || 'ce membre'} du salon ?`)) return;
    try {
      const updated = await api.chatRemoveMember(currentRoom.id, userId);
      setMembers(updated);
      toast('Membre retiré', 'success');
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleChangeIcon = async (file) => {
    if (!currentRoom || !file) return;
    try {
      const formData = new FormData();
      formData.append('icon', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/chat/rooms/${currentRoom.id}/icon`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error('Erreur');
      const data = await res.json();
      setCurrentRoom(prev => ({ ...prev, icon_path: data.icon_path }));
      loadRooms();
      toast('Icône modifiée', 'success');
      setShowChangeIcon(false);
    } catch (e) { toast('Erreur upload', 'error'); }
  };

  const handleContextMenu = (e, m) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, msg: m });
  };

  const startEdit = (m) => { setEditMsg(m); setMsg(m.content); setContextMenu(null); inputRef.current?.focus(); };
  const startReply = (m) => { setReplyTo(m); setContextMenu(null); inputRef.current?.focus(); };
  const deleteMessage = (m) => { const s = getSocket(); if (s) s.emit('delete_message', { message_id: m.id }); setContextMenu(null); };
  const sendReaction = (msgId, emoji) => { const s = getSocket(); if (s) s.emit('react_message', { message_id: msgId, emoji }); };
  const insertEmoji = (e) => { setMsg(prev => prev + e); inputRef.current?.focus(); };

  const fmtTime = (dt) => { const d = new Date(dt + 'Z'); const now = new Date(); const isToday = d.toDateString() === now.toDateString(); const t = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); return isToday ? t : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' + t; };
  const isMobile = window.innerWidth < 768;
  const onlineMembers = members.filter(m => onlineUserIds.includes(m.id));

  // Read receipt ticks
  const getReadStatus = (m) => {
    if (m.user_id !== user.id) return null;
    const readers = (m.read_by || []).filter(r => r.user_id !== user.id);
    const memberCount = members.length - 1;
    if (memberCount <= 0) return 'sent';
    if (readers.length >= memberCount) return 'read_all';
    if (readers.length > 0) return 'read_some';
    return 'sent';
  };

  return (
    <div className="chat-container">
      {/* ===== SIDEBAR ===== */}
      <div className={`chat-sidebar ${isMobile && currentRoom ? 'hidden' : ''}`}>
        <div className="chat-sb-header">
          <span className="chat-sb-title">💬 Messages</span>
        </div>
        {loading ? <div className="chat-sb-loading">Chargement...</div> :
        rooms.length === 0 ? <div className="chat-sb-empty">Aucun salon</div> :
        <div className="chat-sb-list">
          {rooms.map(r => (
            <div key={r.id} className={`chat-sb-room ${currentRoom?.id === r.id ? 'active' : ''}`} onClick={() => setCurrentRoom(r)}>
              <div className="chat-sb-room-avatar">
                <ProtectedRoomIcon room={r} className="chat-room-icon" />
              </div>
              <div className="chat-sb-room-info">
                <div className="chat-sb-room-top">
                  <span className="chat-sb-room-name">{r.name}</span>
                  {r.last_message_at && <span className="chat-sb-room-time">{fmtTime(r.last_message_at)}</span>}
                </div>
                <div className="chat-sb-room-bottom">
                  <span className="chat-sb-room-preview">
                    {r.last_message ? <><strong>{r.last_message_by}:</strong> {r.last_message.substring(0, 35)}{r.last_message.length > 35 ? '…' : ''}</> : <em>Aucun message</em>}
                  </span>
                  {r.unread_count > 0 && <span className="chat-sb-badge">{r.unread_count}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>}
      </div>

      {/* ===== MAIN ===== */}
      <div className={`chat-main ${isMobile && !currentRoom ? 'hidden' : ''}`}>
        {!currentRoom ? (
          <div className="chat-empty"><div style={{ fontSize: '4rem' }}>💬</div><p>Sélectionnez une conversation</p></div>
        ) : (<>
          {/* Header - always visible / sticky */}
          <div className="chat-hdr">
            <div className="chat-hdr-left">
              {isMobile && <button className="chat-hdr-back" onClick={() => setCurrentRoom(null)}>◀</button>}
              <div className="chat-hdr-avatar">
                <ProtectedRoomIcon room={currentRoom} className="chat-room-icon" />
              </div>
              <div className="chat-hdr-info">
                <div className="chat-hdr-name">{currentRoom.name}</div>
              </div>
            </div>

            {/* Members inline — hidden admins filtered for non-admin users */}
            <div className="chat-hdr-members">
              {members.filter(m => isAdmin || m.visible !== 0).map(m => (
                <div key={m.id} className={`chat-hdr-member ${m.visible === 0 ? 'chat-hdr-member-hidden' : ''}`} title={m.username + (m.visible === 0 ? ' (masqué)' : '')}>
                  <span className={`chat-presence-dot ${onlineUserIds.includes(m.id) ? 'online' : 'offline'}`} />
                  <span className="chat-hdr-member-name">{m.username}</span>
                </div>
              ))}
            </div>

            {/* Admin room management dropdown */}
            {isAdmin && (
              <div className="chat-room-mgmt" ref={roomMenuRef}>
                <button className="chat-hdr-btn" onClick={(e) => { e.stopPropagation(); setShowRoomMenu(!showRoomMenu); }}>⚙️</button>
                {showRoomMenu && (
                  <div className="chat-room-mgmt-dropdown" onClick={e => e.stopPropagation()}>
                    <div className="chat-room-mgmt-title">Gestion du salon</div>
                    {currentRoom.type === 'custom' && <button onClick={() => { setShowAddMember(true); setShowRoomMenu(false); api.getUsers().then(u => setAllUsers(u.filter(x => x.active))).catch(() => {}); }}>➕ Ajouter des membres</button>}
                    {currentRoom.type === 'custom' && <button onClick={() => { setShowRemoveMember(true); setShowRoomMenu(false); }}>➖ Retirer des membres</button>}
                    <button onClick={() => { setShowChangeIcon(true); setShowRoomMenu(false); }}>🖼️ Changer l'icône du salon</button>
                    <button onClick={() => { setShowAdminVisibility(true); setShowRoomMenu(false); }}>👑 Gestion des admins</button>
                    {currentRoom.type === 'custom' && (
                      <button className="danger" onClick={() => handleDeleteRoom(currentRoom.id)}>🗑️ Supprimer le salon</button>
                    )}
                    <div style={{ borderTop: '1px solid var(--border-light)', margin: '4px 0' }} />
                    <button onClick={() => { setShowCreateRoom(true); setShowRoomMenu(false); api.getUsers().then(u => setAllUsers(u.filter(x => x.active))).catch(() => {}); }}>🆕 Nouveau salon</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="chat-msgs" onClick={() => setContextMenu(null)}>
            {messages.map((m, i) => {
              if (m.deleted_at) {
                return <div key={m.id} className="chat-msg-deleted">🚫 Message supprimé</div>;
              }
              const isMine = m.user_id === user.id;
              const showHeader = i === 0 || messages[i - 1].user_id !== m.user_id || messages[i - 1].deleted_at;
              const readStatus = getReadStatus(m);
              return (
                <div key={m.id} className={`chat-bubble-wrap ${isMine ? 'mine' : 'other'}`}>
                  {!isMine && showHeader && <div className="chat-bubble-author" style={{ color: m.color }}>{m.username}</div>}
                  <div className={`chat-bubble ${isMine ? 'mine' : 'other'}`}
                    onContextMenu={(e) => handleContextMenu(e, m)}
                    onClick={(e) => { if (isMobile) { e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, msg: m }); } }}>

                    {/* Reply preview */}
                    {m.reply_to && (
                      <div className="chat-reply-preview">
                        <strong>{m.reply_to.username}</strong>
                        <span>{m.reply_to.content?.substring(0, 50) || `📎 ${m.reply_to.file_name}`}</span>
                      </div>
                    )}

                    {/* File/image */}
                    <ProtectedAttachment message={m} />

                    {m.content && <div className="chat-bubble-text">{m.content}</div>}

                    <div className="chat-bubble-meta">
                      {m.edited_at && <span className="chat-edited">modifié</span>}
                      <span className="chat-bubble-time">{fmtTime(m.created_at)}</span>
                      {/* Read ticks */}
                      {isMine && readStatus && (
                        <span className={`chat-ticks ${readStatus}`} onClick={(e) => { e.stopPropagation(); if (isAdmin) setShowReaders(showReaders === m.id ? null : m.id); }}>
                          {readStatus === 'sent' ? '✓' : '✓✓'}
                        </span>
                      )}
                    </div>

                    {/* Reactions */}
                    {m.reactions && Object.keys(m.reactions).length > 0 && (
                      <div className="chat-reactions">
                        {Object.entries(m.reactions).map(([emoji, users]) => (
                          <span key={emoji} className="chat-reaction" title={users.map(u => u.username).join(', ')}>{emoji} {users.length}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Read by details (admin only) */}
                  {isAdmin && showReaders === m.id && m.read_by?.length > 0 && (
                    <div className="chat-readers">
                      Lu par : {m.read_by.filter(r => r.user_id !== user.id).map(r => r.username).join(', ') || 'personne'}
                    </div>
                  )}

                  {/* Quick reactions */}
                  {contextMenu?.msg?.id === m.id && (
                    <div className="chat-quick-react">
                      {QUICK_REACTIONS.map(e => <button key={e} onClick={(ev) => { ev.stopPropagation(); sendReaction(m.id, e); setContextMenu(null); }}>{e}</button>)}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Context menu */}
          {contextMenu && (
            <div className="chat-context" style={{ top: Math.min(contextMenu.y, window.innerHeight - 180), left: Math.min(contextMenu.x, window.innerWidth - 160) }} onClick={e => e.stopPropagation()}>
              <button onClick={() => startReply(contextMenu.msg)}>↩️ Répondre</button>
              {contextMenu.msg.user_id === user.id && !contextMenu.msg.deleted_at && <button onClick={() => startEdit(contextMenu.msg)}>✏️ Modifier</button>}
              {(contextMenu.msg.user_id === user.id || isAdmin) && !contextMenu.msg.deleted_at && <button onClick={() => deleteMessage(contextMenu.msg)} className="danger">🗑️ Supprimer</button>}
            </div>
          )}

          {/* Typing */}
          {typingUsers.length > 0 && (
            <div className="chat-typing-bar">{typingUsers.map(t => t.username).join(', ')} écri{typingUsers.length > 1 ? 'vent' : 't'}...</div>
          )}

          {/* Reply/Edit banner */}
          {(replyTo || editMsg) && (
            <div className="chat-reply-bar">
              <div className="chat-reply-bar-content">
                {replyTo && <><span className="chat-reply-bar-label">↩️ Réponse à <strong>{replyTo.username}</strong></span><span>{replyTo.content?.substring(0, 50)}</span></>}
                {editMsg && <><span className="chat-reply-bar-label">✏️ Modification</span><span>{editMsg.content?.substring(0, 50)}</span></>}
              </div>
              <button className="chat-reply-bar-close" onClick={() => { setReplyTo(null); setEditMsg(null); setMsg(''); }}>✕</button>
            </div>
          )}

          {/* Input */}
          <div className="chat-input-wrapper">
            <div className="chat-input-area">
            <button className="chat-input-btn" onClick={() => setShowEmoji(!showEmoji)}>😊</button>
            <button className="chat-input-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? '⏳' : '📎'}</button>
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleFile} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" />
            <textarea ref={inputRef} className="chat-input-field" value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={handleKeyDown}
              placeholder={editMsg ? 'Modifier le message...' : 'Écrivez un message...'} rows={1} />
            <button className="chat-send-btn" onClick={handleSend} disabled={!msg.trim() && !editMsg}>
              {editMsg ? '✏️' : '➤'}
            </button>
            </div>{/* /chat-input-area */}
          </div>{/* /chat-input-wrapper */}

          {/* Emoji picker */}
          {showEmoji && (
            <div className="chat-emoji-picker">
              <div className="chat-emoji-tabs">
                {Object.keys(EMOJI_CATEGORIES).map(cat => (
                  <button key={cat} className={`chat-emoji-tab ${emojiCat === cat ? 'active' : ''}`} onClick={() => setEmojiCat(cat)}>{cat}</button>
                ))}
              </div>
              <div className="chat-emoji-grid">
                {EMOJI_CATEGORIES[emojiCat].map(e => (
                  <button key={e} className="chat-emoji-btn" onClick={() => insertEmoji(e)}>{e}</button>
                ))}
              </div>
            </div>
          )}
        </>)}
      </div>

      {/* Change icon modal */}
      {showChangeIcon && (
        <div className="modal-overlay" onClick={() => setShowChangeIcon(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header"><h2>🖼️ Changer l'icône</h2><button className="modal-close" onClick={() => setShowChangeIcon(false)}>&times;</button></div>
            <div style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ marginBottom: 16 }}>
                <div className="chat-icon-upload" style={{ width: 80, height: 80, margin: '0 auto' }} onClick={() => document.getElementById('change-icon-input').click()}>
                  <ProtectedRoomIcon room={currentRoom} className="chat-room-icon" />
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 8 }}>Cliquez pour choisir une image PNG ou JPEG</div>
              </div>
              <input id="change-icon-input" type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={(e) => {
                if (e.target.files[0]) handleChangeIcon(e.target.files[0]);
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Admin visibility modal */}
      {showAdminVisibility && (
        <div className="modal-overlay" onClick={() => setShowAdminVisibility(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header"><h2>👑 Gestion des admins</h2><button className="modal-close" onClick={() => setShowAdminVisibility(false)}>&times;</button></div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                Masquer ou montrer les admins dans la barre de présence du salon. Les admins masqués restent membres mais n'apparaissent plus dans la barre en haut.
              </p>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {members.filter(m => m.role === 'admin' || m.role === 'both').length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20, fontSize: '0.85rem' }}>Aucun admin dans ce salon</div>
                ) : (
                  members.filter(m => m.role === 'admin' || m.role === 'both').map(m => {
                    const isVisible = m.visible !== 0;
                    return (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 4px', borderBottom: '1px solid var(--border-light)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span className={`chat-presence-dot ${onlineUserIds.includes(m.id) ? 'online' : 'offline'}`} />
                          <span style={{ fontSize: '0.92rem', fontWeight: 600 }}>{m.username}</span>
                          <span style={{ fontSize: '0.7rem', color: isVisible ? 'var(--accent-green, #22c55e)' : 'var(--text-muted)' }}>{isVisible ? '👁️ Visible' : '🚫 Masqué'}</span>
                        </div>
                        <button
                          className={`btn btn-sm ${isVisible ? 'btn-secondary' : 'btn-primary'}`}
                          style={{ padding: '4px 14px', fontSize: '0.78rem' }}
                          onClick={async () => {
                            try {
                              const updated = await api.chatToggleVisibility(currentRoom.id, m.id, !isVisible);
                              setMembers(updated);
                              toast(isVisible ? `${m.username} masqué` : `${m.username} visible`, 'success');
                            } catch (e) { toast('Erreur', 'error'); }
                          }}
                        >{isVisible ? 'Masquer' : 'Montrer'}</button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add member modal */}
      {showAddMember && (
        <div className="modal-overlay" onClick={() => setShowAddMember(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>➕ Ajouter des membres</h2><button className="modal-close" onClick={() => setShowAddMember(false)}>&times;</button></div>
            <div style={{ padding: 20 }}>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {allUsers.filter(u => !members.find(m => m.id === u.id)).length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20, fontSize: '0.85rem' }}>Tous les utilisateurs sont déjà membres</div>
                ) : (
                  allUsers.filter(u => !members.find(m => m.id === u.id)).map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid var(--border-light)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: u.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.85rem' }}>{u.username}</span>
                      </div>
                      <button className="btn btn-primary btn-sm" style={{ padding: '3px 12px', fontSize: '0.75rem' }} onClick={() => handleAddMembers([u.id])}>Ajouter</button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove member modal */}
      {showRemoveMember && (
        <div className="modal-overlay" onClick={() => setShowRemoveMember(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>➖ Retirer des membres</h2><button className="modal-close" onClick={() => setShowRemoveMember(false)}>&times;</button></div>
            <div style={{ padding: 20 }}>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {members.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className={`chat-presence-dot ${onlineUserIds.includes(m.id) ? 'online' : 'offline'}`} />
                      <span style={{ fontSize: '0.85rem' }}>{m.username}</span>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{m.role === 'admin' || m.role === 'both' ? '⚡' : m.role === 'responsable' ? '🔑' : ''}</span>
                    </div>
                    <button className="btn btn-secondary btn-sm" style={{ padding: '3px 12px', fontSize: '0.75rem', color: 'var(--accent-red)' }} onClick={() => handleRemoveMember(m.id)}>Retirer</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create room modal */}
      {showCreateRoom && (
        <div className="modal-overlay" onClick={() => { setShowCreateRoom(false); setRoomIcon(null); setRoomIconPreview(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
            <div className="modal-header"><h2>🆕 Nouveau salon</h2><button className="modal-close" onClick={() => { setShowCreateRoom(false); setRoomIcon(null); setRoomIconPreview(null); }}>&times;</button></div>
            <div style={{ padding: 20 }}>
              {/* Icon upload */}
              <div className="form-group" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div className="chat-icon-upload" onClick={() => document.getElementById('room-icon-input').click()}>
                  {roomIconPreview ? (
                    <img src={roomIconPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    <span style={{ fontSize: '1.8rem' }}>💬</span>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Icône du salon</label>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>PNG ou JPEG, optionnel</div>
                  <input id="room-icon-input" type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) { setRoomIcon(file); setRoomIconPreview(URL.createObjectURL(file)); }
                  }} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Nom du salon</label>
                <input className="form-input" value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="ex: Général, Formation..." />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Membres</label>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-sm)', padding: 8 }}>
                  {allUsers.map(u => (
                    <label key={u.id} className="form-checkbox" style={{ fontSize: '0.82rem', marginBottom: 4 }}>
                      <input type="checkbox" checked={selectedMembers.includes(u.id)} onChange={() => setSelectedMembers(prev => prev.includes(u.id) ? prev.filter(i => i !== u.id) : [...prev, u.id])} />
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: u.color }} />{u.username}</span>
                    </label>
                  ))}
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleCreateRoom} disabled={!newRoomName.trim()} style={{ width: '100%' }}>Créer le salon</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
